import React from 'react';
import { Link } from 'react-router-dom';
import { Clipboard, BarChart3, Cloud } from 'lucide-react';

const Home = () => {
  const processStats = [
    { number: "Assess", label: "Current Workflows" },
    { number: "Design", label: "Optimal Solutions" },
    { number: "Implement", label: "With Minimal Disruption" },
    { number: "Support", label: "Ongoing Success" }
  ];

  const services = [
    {
      icon: <Clipboard className="w-12 h-12 text-blue-400" />,
      title: "Jira Workflow Optimization",
      description: "Streamline processes across all departments with customized Jira workflows that boost productivity and visibility for every team.",
      features: [
        "Custom workflow design for different departments",
        "Automated approval processes",
        "Real-time reporting and dashboards",
        "Integration with existing tools",
        "Training and change management",
        "Ongoing optimization support"
      ]
    },
    {
      icon: <BarChart3 className="w-12 h-12 text-green-400" />,
      title: "Business Process Automation",
      description: "Automate repetitive tasks and approval processes to free up your teams for strategic work that drives business value.",
      features: [
        "Process mapping and analysis",
        "Automation workflow design",
        "Integration with Jira, Confluence, and other tools",
        "Compliance and audit trail setup",
        "Performance monitoring and optimization",
        "Scalable automation frameworks"
      ]
    },
    {
      icon: <Cloud className="w-12 h-12 text-purple-400" />,
      title: "Cloud Infrastructure Management",
      description: "Secure, scalable cloud solutions that support your business operations without the technical complexity.",
      features: [
        "AWS cloud architecture design",
        "Security and compliance implementation",
        "Cost optimization strategies",
        "Disaster recovery planning",
        "Monitoring and maintenance",
        "24/7 support and SLA guarantees"
      ]
    }
  ];

  const businessFocus = [
    {
      icon: "🏢",
      title: "Executive Leadership",
      description: "Gain real-time visibility into project progress, resource allocation, and ROI across all departments."
    },
    {
      icon: "👥",
      title: "HR & Operations",
      description: "Streamline employee onboarding, performance reviews, and operational workflows with intuitive process management."
    },
    {
      icon: "💰",
      title: "Finance & Legal",
      description: "Ensure compliance, track budgets, and manage contracts with automated approval workflows and audit trails."
    },
    {
      icon: "🛒",
      title: "Sales & Marketing",
      description: "Align campaigns with product development, track customer feedback, and manage pipeline visibility across teams."
    }
  ];

  return (
    <div>
      <section className="pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
              Business Workflow
              <span className="pb-6 block bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
                Optimization Experts
              </span>
            </h1>
            <p className="text-lg sm:text-xl text-slate-300 mb-16 max-w-3xl mx-auto leading-relaxed">
              Transform how your entire organization works with expert Jira implementation and cloud solutions that connect every department - from executives to operations.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-16">
              <Link to="/contact" className="bg-gradient-to-r from-blue-600 to-cyan-600 text-white px-8 py-4 rounded-lg font-semibold hover:from-blue-700 hover:to-cyan-700 transition-all duration-300 transform hover:scale-105 text-center">
                Schedule Free Consultation
              </Link>
              <Link to="/about" className="border border-slate-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-slate-800 transition-all duration-300 text-center">
                See How We Help
              </Link>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto">
              {processStats.map((stat, index) => (
                <div key={index} className="text-center">
                  <div className="text-xl font-bold text-blue-400 mb-2">{stat.number}</div>
                  <div className="text-slate-400 text-sm">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Services Section with Fancy Hover Effects */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-800/50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">Workflow Solutions for Every Team</h2>
            <p className="text-lg text-slate-300 max-w-2xl mx-auto">
              Specialized Jira and cloud implementations that drive business results across your entire organization
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div 
                key={index} 
                className="bg-slate-800/80 backdrop-blur-sm rounded-xl p-8 border border-slate-700 transition-all duration-300 hover:border-blue-500 hover:scale-[1.02] hover:shadow-xl hover:shadow-blue-500/10"
              >
                <div className="mb-6">{service.icon}</div>
                <h3 className="text-xl font-bold text-white mb-4">{service.title}</h3>
                <p className="text-slate-300 mb-6 leading-relaxed">{service.description}</p>
                
                {/* Always visible features with fancy styling */}
                <div className="space-y-3 pt-4">
                  {service.features.map((feature, featureIndex) => (
                    <div key={featureIndex} className="flex items-start group">
                      <div className="flex-shrink-0 mt-1">
                        <svg className="w-5 h-5 text-blue-400 group-hover:text-blue-300 transition-colors duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                        </svg>
                      </div>
                      <span className="ml-3 text-slate-300 group-hover:text-white transition-colors duration-200">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Business Focus Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">Transform Every Department</h2>
            <p className="text-lg text-slate-300 max-w-2xl mx-auto">
              Specialized workflow solutions that connect and optimize operations across your entire organization
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {businessFocus.map((focus, index) => (
              <div 
                key={index} 
                className="bg-slate-800/80 backdrop-blur-sm rounded-xl p-8 border border-slate-700 text-center transition-all duration-300 hover:border-cyan-500 hover:scale-[1.03] hover:shadow-lg hover:shadow-cyan-500/10"
              >
                <div className="text-4xl mb-6">{focus.icon}</div>
                <h3 className="text-xl font-bold text-white mb-4">{focus.title}</h3>
                <p className="text-slate-300 leading-relaxed">{focus.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;